import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Minimize2 } from 'lucide-react';
import { posthog } from 'posthog-js';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DeviceSelection from './deviceSelection';
import RecorderControls from './RecorderControls';
import Notepad from './Notepad';
import { RootState } from '../store';
import PopupRecorder from './PopupRecorder';
import { setUIMode } from '../recorderSlice';
import { Z_INDEX } from '../../constants/zIndex';
import SkipDeviceSelection from './skipDeviceSelection';
import { streams } from './utils/streamUtils';
import useUploadPulse from '../hooks/useUploadPulse';

// Audio level detection utilities
interface AudioLevelDetectorConfig {
  minVolumeThreshold: number; // Minimum volume level to consider "active" (0-255)
  silentDurationThreshold: number; // Time in ms before considering recording as silent
  sampleRate: number; // How often to check audio levels (ms)
}

const defaultAudioLevelConfig: AudioLevelDetectorConfig = {
  minVolumeThreshold: 1, // Very low threshold for normal speech detection
  silentDurationThreshold: 5000, // 5 seconds before warning
  sampleRate: 200, // Check every 200ms (less frequent)
};

class AudioLevelDetector {
  private analyser: AnalyserNode;

  private dataArray: Uint8Array;

  private isDetecting: boolean = false;

  private intervalId: ReturnType<typeof setInterval> | null = null;

  private silentStartTime: number | null = null;

  private config: AudioLevelDetectorConfig;

  private onSilentRecording: () => void;

  private onAudioDetected: () => void;

  constructor(
    analyser: AnalyserNode,
    configParam: AudioLevelDetectorConfig = defaultAudioLevelConfig,
    onSilentRecording: () => void,
    onAudioDetected: () => void,
  ) {
    this.analyser = analyser;
    this.config = configParam;
    this.onSilentRecording = onSilentRecording;
    this.onAudioDetected = onAudioDetected;
    this.dataArray = new Uint8Array(analyser.frequencyBinCount);
  }

  start(): void {
    if (this.isDetecting) return;

    this.isDetecting = true;
    this.silentStartTime = Date.now();

    this.intervalId = setInterval(() => {
      this.checkAudioLevel();
    }, this.config.sampleRate);
  }

  stop(): void {
    if (!this.isDetecting) return;

    this.isDetecting = false;
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.silentStartTime = null;
  }

  private checkAudioLevel(): void {
    if (!this.analyser || !this.dataArray) return;

    try {
      this.analyser.getByteFrequencyData(this.dataArray);

      // Calculate average volume level
      const average =
        this.dataArray.reduce((sum, value) => sum + value, 0) /
        this.dataArray.length;

      // Debug logging (remove in production)
      if (Math.random() < 0.02) {
        // Log only 2% of the time to avoid spam
        console.log(
          `Audio level: ${average.toFixed(2)}, threshold: ${
            this.config.minVolumeThreshold
          }, detected: ${average > this.config.minVolumeThreshold}`,
        );
      }

      if (average > this.config.minVolumeThreshold) {
        // Audio detected, reset silent timer
        this.silentStartTime = null;
        this.onAudioDetected();
      } else if (this.silentStartTime === null) {
        this.silentStartTime = Date.now();
      } else {
        const silentDuration = Date.now() - this.silentStartTime;
        if (silentDuration > this.config.silentDurationThreshold) {
          this.onSilentRecording();
          this.silentStartTime = Date.now(); // Reset to avoid repeated alerts
        }
      }
    } catch (error) {
      console.error('Error checking audio level:', error);
      this.stop();
    }
  }
}

const RecorderComponent = () => {
  const dispatch = useDispatch();
  const { status, controls, settings } = useSelector(
    (state: RootState) => state.recorder,
  );
  const [showNotes, setShowNotes] = useState(() => {
    const savedValue = localStorage.getItem('notepad-visible');
    return savedValue ? JSON.parse(savedValue) : true;
  });

  // Audio level detection state
  const [, setAudioLevelWarning] = useState(false);
  const [, setHasDetectedAudio] = useState(false);
  const audioLevelDetector = useRef<AudioLevelDetector | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const hasDetectedAudioRef = useRef<boolean>(false);

  // Recording analytics tracking
  const recordingStartTime = useRef<number | null>(null);

  const isBackdropInvisible = status === 'stopped';
  const showDeviceSelection = status === 'initializing' || status === 'ready';
  const isSaving = status === 'saving';
  const isRecording = status === 'recording';
  const { uiMode, isMountedFromPlugin } = controls;
  const { webCam, screen } = settings?.videoSettings || {};
  const { microphoneAudio } = settings?.audioSettings || {};

  // Audio level detection callbacks
  const handleSilentRecording = () => {
    console.log('Silent recording detected - no audio for 5 seconds');
    if (isRecording && !hasDetectedAudioRef.current) {
      setAudioLevelWarning(true);
    }
  };

  const handleAudioDetected = () => {
    console.log('Audio detected!');
    hasDetectedAudioRef.current = true;
    setHasDetectedAudio(true);
    setAudioLevelWarning(false);
  };

  // Heartbeat interval to keep backend aware of ongoing upload
  useUploadPulse();

  useEffect(() => {
    const isActiveRecording = status === 'recording' || status === 'paused';

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isActiveRecording) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  // Audio level detection setup
  useEffect(() => {
    const setupAudioLevelDetection = async () => {
      if (isRecording && microphoneAudio && streams.userMediaStream) {
        try {
          // Reset detection state and start timing
          hasDetectedAudioRef.current = false;
          setHasDetectedAudio(false);
          setAudioLevelWarning(false);
          recordingStartTime.current = Date.now();

          // Check if the stream has audio tracks
          const audioTracks = streams.userMediaStream.getAudioTracks();
          if (audioTracks.length === 0) {
            console.warn('No audio tracks found in user media stream');
            return;
          }

          // Set up audio context and analyser
          audioContext.current = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
          analyser.current = audioContext.current.createAnalyser();
          const source = audioContext.current.createMediaStreamSource(
            streams.userMediaStream,
          );
          source.connect(analyser.current);

          analyser.current.fftSize = 2048;
          analyser.current.minDecibels = -90;
          analyser.current.maxDecibels = -10;
          analyser.current.smoothingTimeConstant = 0.85;

          // Use a more appropriate threshold for microphone audio
          const microphoneAudioConfig = {
            minVolumeThreshold: 1, // Very low threshold for normal speech detection
            silentDurationThreshold: 5000, // 5 seconds before warning
            sampleRate: 200, // Check every 200ms (less frequent)
          };

          // Initialize audio level detector
          audioLevelDetector.current = new AudioLevelDetector(
            analyser.current,
            microphoneAudioConfig,
            handleSilentRecording,
            handleAudioDetected,
          );

          // Start detection
          audioLevelDetector.current.start();
          console.log('Audio level detection started for microphone');
        } catch (error) {
          console.error('Error setting up audio level detection:', error);
        }
      }
    };

    const cleanupAudioLevelDetection = () => {
      // Track analytics before cleanup if recording ended
      if (recordingStartTime.current && microphoneAudio) {
        const recordingDuration = Date.now() - recordingStartTime.current;
        const recordingDurationSeconds = recordingDuration / 1000;

        // Track if recording was > 20 seconds and no audio detected
        if (recordingDurationSeconds > 20 && !hasDetectedAudioRef.current) {
          posthog.capture('microphone-recording-no-audio', {
            recordingDurationSeconds: Math.round(recordingDurationSeconds),
            recordingDurationMs: recordingDuration,
            hasDetectedAudio: false,
            microphoneEnabled: true,
            timestamp: new Date().toISOString(),
          });
          console.log(
            `PostHog: Tracked microphone recording with no audio detected (${recordingDurationSeconds}s)`,
          );
        }
      }

      if (audioLevelDetector.current) {
        audioLevelDetector.current.stop();
        audioLevelDetector.current = null;
      }
      if (audioContext.current?.state !== 'closed') {
        audioContext.current?.close();
      }
      audioContext.current = null;
      analyser.current = null;
      hasDetectedAudioRef.current = false;
      setHasDetectedAudio(false);
      setAudioLevelWarning(false);
      recordingStartTime.current = null;
    };

    if (isRecording && microphoneAudio) {
      setupAudioLevelDetection();
    } else {
      cleanupAudioLevelDetection();
    }

    return cleanupAudioLevelDetection;
  }, [isRecording, microphoneAudio]);

  const handleToggleNotes = () => {
    setShowNotes((prev: boolean) => {
      const newValue = !prev;
      localStorage.setItem('notepad-visible', JSON.stringify(newValue));
      return newValue;
    });
  };

  const handleSwitchToPopup = () => {
    dispatch(setUIMode('popup'));
  };

  if (showDeviceSelection) {
    // If no screen or webcam is selected, record audio will start instantly
    if (!webCam && !screen) {
      return <SkipDeviceSelection />;
    }
    return <DeviceSelection />;
  }

  // Use the popup UI mode if selected
  if (uiMode === 'popup' && !showDeviceSelection) {
    return <PopupRecorder />;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-start justify-center bg-background',
        isBackdropInvisible && !isSaving && 'invisible',
      )}
      style={{ zIndex: Z_INDEX.popover }}
    >
      <div
        className={cn(
          'relative flex flex-col w-full max-w-[600px] h-full p-2 transition-opacity',
          isBackdropInvisible && !isSaving ? 'opacity-0' : 'opacity-100',
        )}
      >
        {/* Minimize button - only shown when mounted from plugin */}
        {isMountedFromPlugin && (
          <div className="absolute top-2.5 right-2.5 z-10">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSwitchToPopup}
              className="h-8 w-8 p-0 bg-black/10 hover:bg-black/20"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        )}

        <RecorderControls
          onToggleNotes={handleToggleNotes}
          showNotes={showNotes}
        />

        <div
          className={cn(
            'w-full flex overflow-hidden transition-all duration-200',
            showNotes ? 'flex-1' : 'flex-none',
          )}
        >
          {showNotes && (
            <div className="flex-1 min-h-0">
              <Notepad />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecorderComponent;
